import React, { useContext, useMemo } from "react";

import { Link as MuiLink } from "@mui/material";
import { Link, useLocation } from "react-router-dom";

import { SettingValue } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/settings_pb";

import { NO_DISPLAY_DATA, scvLink } from "../../../../constants/index";
import { toUUID } from "../../../../helpers/s12-helpers";
import {
  convertTimestampToDate,
  formatDateTimeDisplay,
} from "../../../../helpers/time";
import { displaySensorDetails } from "../../../../pages/org-sensors";
import OrgIndustryVertical from "../../details-display-cards/org-industry-vertical";
import OrgDefaultTemplateAccess from "../../tabs/components/org-default-template-access";
import OrgInspectionQuotaDisplay from "../../tabs/components/org-inspection-quota";
import SuspendLiftSuspensionOrgButton from "../../tabs/components/suspend-lift-suspension-org-button";
import { CardShell, Copyable, Field, FieldGrid, IdValue } from "./card-shell";
import { ScimStatus, SsoStatus } from "./sso-scim-status";
import { OrgScimData } from "api/endpoints/scim";
import { SsoAdminData } from "api/endpoints/sso";
import {
  OrgDataContext,
  OrgDetailsContext,
  OrgHasSensorsContext,
} from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";

/** Setting keys, mirroring the ones the full detail cards read. */
const LEGAL_AND_SECURITY_EMAIL_SETTING_KEY =
  "organization.legal_and_security_notification_email";

export interface OrgCardProps {
  regionCode: string;
  orgSettings: SettingValue[];
  ssoData?: SsoAdminData;
  scimData?: OrgScimData;
  refreshOrgData?: () => void;
}

const readStringSetting = (
  settings: SettingValue[],
  key: string,
): string | undefined => {
  const setting = settings.find((entry) => entry.key === key);
  return setting?.value?.value.case === "stringValue"
    ? setting.value.value.value
    : undefined;
};

export interface IdentityCardProps extends OrgCardProps {
  /** Rendered in the title bar: it is wide, and the body is for data. */
  action?: React.ReactNode;
}

export const IdentityCard = ({
  regionCode,
  orgSettings,
  action,
}: IdentityCardProps) => {
  const { orgData } = useContext(OrgDataContext);
  const { copyToClipboard } = useContext(SnackbarContext);

  const legalAndSecurityEmail = useMemo(
    () => readStringSetting(orgSettings, LEGAL_AND_SECURITY_EMAIL_SETTING_KEY),
    [orgSettings],
  );

  const created = orgData.createdAt
    ? formatDateTimeDisplay(convertTimestampToDate(orgData.createdAt))
    : NO_DISPLAY_DATA;

  return (
    <CardShell
      title="Identity and structure"
      source="Org"
      action={action}
      testId="dashboard-card-identity"
    >
      <FieldGrid>
        {/* One line rather than two, and the UUID form leads: it is what the
            other tools around here take. Shortened like every other id here, so
            a narrower card cannot eat the copy control - the whole value is on
            the tooltip and on the clipboard. The prefixed form is a copy action
            behind the s12 chip. */}
        <Field label="Org ID" size="half">
          {orgData.id ? (
            <IdValue
              value={toUUID(orgData.id)}
              label="org UUID"
              onCopy={copyToClipboard}
              alternate={{ value: orgData.id, label: "s12" }}
            />
          ) : (
            NO_DISPLAY_DATA
          )}
        </Field>
        <Field label="Users" size="sm">
          <span data-testid="org-details-team-structure-total-users">
            {orgData.members?.length || NO_DISPLAY_DATA}
          </span>
        </Field>
        {/* md, so the row still holds the whole id, the count and the picker. */}
        <Field label="Industry vertical" size="md">
          <OrgIndustryVertical
            regionCode={regionCode}
            orgId={orgData.id}
            orgSettings={orgSettings}
          />
        </Field>
        <Field label="Organisation owner email" size="half">
          {orgData.info?.email ? (
            <Copyable
              value={orgData.info.email}
              label="owner email"
              onCopy={copyToClipboard}
            />
          ) : (
            NO_DISPLAY_DATA
          )}
        </Field>
        <Field label="Legal and security notifications email" size="half">
          {legalAndSecurityEmail ? (
            <Copyable
              value={legalAndSecurityEmail}
              label="legal and security email"
              onCopy={copyToClipboard}
            />
          ) : (
            NO_DISPLAY_DATA
          )}
        </Field>
        {/* Half each, like the rows above, so every second column in the card
            starts on the same edge. */}
        <Field label="Date created" size="half">
          {created}
        </Field>
        <Field label="Data centre" size="half">
          {regionCode.toUpperCase()}
        </Field>
      </FieldGrid>
    </CardShell>
  );
};

export const AccessSecurityCard = ({
  regionCode,
  ssoData,
  scimData,
  refreshOrgData,
}: OrgCardProps) => {
  const { orgDetails, setOrgDetails } = useContext(OrgDetailsContext);

  const handleDefaultTemplateAccessUpdate = (newValue: string) => {
    setOrgDetails((previous) => {
      const updated = previous.clone();
      updated.defaultTemplateAccess = newValue;
      return updated;
    });
  };

  return (
    <CardShell
      title="Access &amp; security"
      source="Org"
      testId="dashboard-card-access"
    >
      <FieldGrid>
        {/* Labels and values stay word-for-word what the detail pane shows —
            these are terms people search for and quote in tickets. */}
        <Field label="SSO" size="md">
          <SsoStatus ssoData={ssoData} />
        </Field>
        <Field label="SCIM" size="md">
          <ScimStatus scimData={scimData} />
        </Field>
        <Field label="Suspend/Lift Suspension" size="md">
          {refreshOrgData ? (
            <SuspendLiftSuspensionOrgButton refreshOrgData={refreshOrgData} />
          ) : (
            NO_DISPLAY_DATA
          )}
        </Field>
        <Field label="Default template access" size="lg">
          <OrgDefaultTemplateAccess
            regionCode={regionCode}
            initialValue={orgDetails?.defaultTemplateAccess ?? ""}
            onUpdate={handleDefaultTemplateAccessUpdate}
          />
        </Field>
      </FieldGrid>
    </CardShell>
  );
};

export const UsageCard = ({ regionCode }: OrgCardProps) => {
  const location = useLocation();
  const { orgHasSensors } = useContext(OrgHasSensorsContext);

  return (
    <CardShell title="Usage" source="Org" testId="dashboard-card-usage">
      <FieldGrid>
        {/* Both states link through to the sensors page, as the pane does -
            "none found" is still worth opening, since that page is where you
            confirm it. Asset_ExistsResult is an enum whose "not exists" member
            is 3, so truthiness would claim every org has sensors. */}
        <Field label="Sensors" size="lg">
          <MuiLink
            component={Link}
            to={{ pathname: `${location.pathname}/sensors` }}
            underline="hover"
            data-testid="dashboard-org-sensors-link"
          >
            {displaySensorDetails(orgHasSensors)
              ? "Organisation contains sensors"
              : "No hardware sensors found"}
          </MuiLink>
        </Field>
        <Field label="Additional org inspection quota" size="lg">
          <OrgInspectionQuotaDisplay regionCode={regionCode} />
        </Field>
      </FieldGrid>
    </CardShell>
  );
};

export const ElsewhereCard = () => {
  const location = useLocation();
  const { orgData } = useContext(OrgDataContext);

  return (
    <CardShell
      title="Miscellaneous"
      source="Links"
      testId="dashboard-card-links"
    >
      <FieldGrid>
        <Field label="History" size="md">
          <MuiLink
            component={Link}
            to={{ pathname: `${location.pathname}/history` }}
            underline="hover"
            data-testid="dashboard-org-history-link"
          >
            Organisation history
          </MuiLink>
        </Field>
        <Field label="Single customer view" size="md">
          {/* The Tableau view lives at a fixed URL in constants, not an env
              var - the org id is the only variable part. */}
          <MuiLink
            href={`${scvLink}${orgData.id}`}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            data-testid="dashboard-org-tableau-scv-link"
          >
            Tableau SCV
          </MuiLink>
        </Field>
      </FieldGrid>
    </CardShell>
  );
};
